import { useQuiz } from '@renderer/lib/useQuiz'
import { preventOrphanPunctuation } from '@renderer/lib/utils'
import { AnimatePresence, motion } from 'motion/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
// import { useNavigate } from 'react-router'
import { Button } from '../ui/button'

const speed = 0.5
const bounce = 0.2
const delay = 0.3
const doorTransition = {
  visualDuration: speed,
  type: 'spring',
  bounce: bounce,
  opacity: { duration: 0.2, delay: delay },
  delay: delay
}

const EndQuestion = () => {
  const {
    isCorrectAnswer,
    score,
    isLastQuestion,
    next,
    validatedAnswers,
    currentQuestion,
    isAnswered
  } = useQuiz()
  const { t } = useTranslation('translation', { keyPrefix: 'quiz' })

  const commentKey = isCorrectAnswer ? 'good_answer_comment' : 'bad_answer_comment'
  const commentImageKey = isCorrectAnswer ? 'good_answer_comment_image' : 'bad_answer_comment_image'
  const commentImageLayoutKey = isCorrectAnswer
    ? 'good_answer_comment_image_layout'
    : 'bad_answer_comment_image_layout'
  const commentImage = currentQuestion
    ? (t(`${currentQuestion.id}.${commentImageKey}`, { defaultValue: '' }) as string)
    : ''
  const commentImageLayout = currentQuestion
    ? (t(`${currentQuestion.id}.${commentImageLayoutKey}`, { defaultValue: 'square' }) as string)
    : 'square'
  const isWideCommentImage = commentImageLayout === 'wide'

  const endQuestion: boolean = useMemo(() => {
    switch (currentQuestion?.type) {
      case 'single':
        return isAnswered
      case 'multiple':
        return isAnswered && validatedAnswers
      default:
        return false
    }
  }, [currentQuestion?.type, isAnswered, validatedAnswers])

  const handleNext = () => {
    if (isLastQuestion) {
      console.log('isLastQuestion')
      next()
      // navigate('/quiz/end', { replace: true })
    } else {
      next()
    }
  }

  return (
    <AnimatePresence mode="wait">
      {endQuestion && (
        <>
          <motion.div
            id="right-door"
            className={`absolute left-1/2 h-full border-r-2 border-y-2 border-white
         ${isCorrectAnswer ? 'bg-ratp-menthe' : 'bg-ratp-rouge'}`}
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: '50%', opacity: 1 }}
            exit={{ width: 0, opacity: 0, transition: { duration: 0.1 } }}
            transition={doorTransition}
          />
          <motion.div
            id="left-door"
            className={`absolute right-1/2 h-full border-l-2 border-y-2 border-white
         ${isCorrectAnswer ? 'bg-ratp-menthe' : 'bg-ratp-rouge'}`}
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: '50%', opacity: 1 }}
            exit={{ width: 0, opacity: 0, transition: { duration: 0.1 } }}
            transition={doorTransition}
          />
          <motion.div
            id="answer-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.1 } }}
            transition={{ visualDuration: 0.7, type: 'spring', bounce: 0.2, delay: 1 }}
            className={`flex flex-col items-center justify-center absolute inset-0 overflow-hidden px-4 py-3 gap-3 [@media(max-height:1100px)]:gap-2 [@media(max-height:1100px)]:px-6 [@media(max-height:1100px)]:py-2 [@media(min-height:1101px)]:gap-4
         ${isCorrectAnswer ? 'text-primary' : 'text-white'}`}
          >
            <h1
              className={`font-display font-bold shrink-0 text-3xl [@media(max-height:1100px)]:text-xl [@media(min-height:1101px)]:text-4xl ${isCorrectAnswer ? 'text-primary' : 'text-white'}`}
            >
              {
                t(isCorrectAnswer ? 'correct_answer' : 'incorrect_answer', {
                  count: score
                } as any) as string
              }
            </h1>
            <div
              className={`flex flex-col items-center text-center min-h-0 overflow-y-auto gap-2 [@media(max-height:1100px)]:gap-1.5 [@media(min-height:1101px)]:gap-4 ${isWideCommentImage ? 'w-[min(92vw,56rem)] max-w-4xl' : 'max-w-2xl'}`}
            >
              <p
                className={`leading-snug text-pretty text-lg [@media(max-height:1100px)]:text-sm [@media(min-height:1101px)]:text-xl ${isCorrectAnswer ? 'text-primary' : 'text-white'}`}
              >
                {preventOrphanPunctuation(
                  t(`${currentQuestion.id}.${commentKey}` as any) as string
                )}
              </p>
              {commentImage && (
                <img
                  src={`${import.meta.env.BASE_URL}assets/images/${commentImage}`}
                  alt=""
                  className={
                    isWideCommentImage
                      ? 'w-auto max-w-full h-auto object-contain shrink-0 max-h-[36vh] [@media(max-height:1100px)]:max-h-[26vh] [@media(max-height:850px)]:max-h-[20vh] [@media(min-height:1101px)]:max-h-[40vh]'
                      : 'bg-white rounded-lg p-1 shrink-0 w-24 h-24 [@media(max-height:1100px)]:w-16 [@media(max-height:1100px)]:h-16 [@media(min-height:1101px)]:w-28 [@media(min-height:1101px)]:h-28'
                  }
                />
              )}
            </div>
            <Button
              className="bg-white text-primary border-primary hover:bg-primary hover:text-white border-4 shrink-0 text-xl p-6 [@media(max-height:1100px)]:text-base [@media(max-height:1100px)]:p-3 [@media(min-height:1101px)]:text-2xl [@media(min-height:1101px)]:p-8"
              onClick={handleNext}
            >
              {isLastQuestion ? t('end') : t('next')}
            </Button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default EndQuestion
